import { TransitionGroup, useSafeCallback, useSafeState, useUnmountRef } from '@atomica.co/components';
import { embedIdInPath, EMPTY, URL } from '@atomica.co/utils';
import {
  BoardEntity,
  BoardId,
  BoardMessageId,
  CONFIRMED,
  toNewMessageNotificationsPath,
  UserEntity
} from '@atomica.co/yosemal';
import React, { useEffect, useRef } from 'react';
import Screen from '../../components/screen/Screen';
import { toBoardIndex } from '../../converters/path-converter';
import { analytics, database } from '../../firebase';
import usePath from '../../redux/hooks/usePath';
import useUser from '../../redux/hooks/useUser';
import { Path, PATH_IDS } from '../../router/Routes';
import BoardDetails, { BoardDetailsRef } from './board-details/BoardDetails';
import { BoardMessageListRef } from './board-details/parts/BoardMessageList';
import { BoardThreadRef } from './board-details/parts/BoardThreadList';
import BoardFixedModal from './board-fixed-modal/BoardFixedModal';
import BoardFooterInput from './board-footer-input/BoardFooterInput';
import BoardFullModal from './board-full-modal/BoardFullModal';
import BoardHeader from './board-header/BoardHeader';
import BoardList, { BoardListRef } from './board-list/BoardList';
import BoardMessage, { BoardMessageRef } from './board-message/BoardMessage';
import BoardPartialModal from './board-partial-modal/BoardPartialModal';
import BoardThread from './board-thread/BoardThread';

export enum BoardIndex {
  BOARD_LIST,
  BOARD_JOIN,
  BOARD_DETAILS,
  BOARD_REPLY,
  OUT_OF_TARGET
}

export enum BoardAction {
  BACK_TO_HOME = 'back_to_home',
  BACK_TO_DETAILS = 'back_to_details',
  BACK_TO_PREVIEW = 'back_to_preview',
  CREATE_BOARD = 'create_board',
  REFRESH_BOARD_LIST = 'refresh_board_list',
  INPUT_BOARD_MESSAGE = 'input_board_message',
  BOARD_MESSAGE_WRITTEN = 'board_message_written',
  OPEN_BOARD_SETTINGS = 'open_board_settings',
  REFRESH_BOARD_DETAILS = 'refresh_board_details',
  OPEN_WISH = 'open_wish',
  INVITE_MEMBERS = 'invite_members',
  SHOW_QR = 'show_qr',
  REFRESH_BOARD_THREADS = 'refresh_board_threads'
}

interface P {}

const HomeScreen: React.FC<P> = React.memo(() => {
  const { getUser } = useUser();
  const { path, params, replacePath } = usePath();
  const boardListRef = useRef<BoardListRef>(null);
  const boardDetailsRef = useRef<BoardDetailsRef>(null);
  const boardMessageListRef = useRef<BoardMessageListRef>(null);
  const boardMessageRef = useRef<BoardMessageRef>(null);
  const boardThreadRef = useRef<BoardThreadRef>(null);
  const unmountRef = useUnmountRef();
  const [isModalOpen, setIsModalOpen] = useSafeState<boolean>(unmountRef, false);
  const [isFullModalOpen, setIsFullModalOpen] = useSafeState<boolean>(unmountRef, false);
  const [isPartialModalOpen, setIsPartialModalOpen] = useSafeState<boolean>(unmountRef, false);
  const [index, setIndex] = useSafeState<BoardIndex>(unmountRef, toBoardIndex(path));
  const [action, setAction] = useSafeState<BoardAction>(unmountRef, EMPTY as BoardAction);
  const [user, setUser] = useSafeState<UserEntity | undefined>(unmountRef);
  const [clickedBoardId, setClickedBoardId] = useSafeState<BoardId | undefined>(unmountRef, params.boardId);
  const [clickedBoardMessageId, setClickedBoardMessageId] = useSafeState<BoardMessageId | undefined>(
    unmountRef,
    params.boardMessageId
  );
  const [clickedPhotoURL, setClickedPhotoURL] = useSafeState<URL>(unmountRef, EMPTY);

  const initialize = useSafeCallback(async (): Promise<void> => {
    const user = await getUser();
    setUser(user);
  }, [getUser, setUser]);

  useEffect(() => {
    initialize();
    analytics.setCurrentScreen('ホーム画面');
  }, [initialize]);

  useEffect(() => {
    setIndex(toBoardIndex(path));
    setClickedBoardId(params.boardId);
    setClickedBoardMessageId(params.boardMessageId);
  }, [setIndex, path, setClickedBoardId, setClickedBoardMessageId, params]);

  const openBoardListScreen = useSafeCallback((): void => {
    replacePath(Path.BOARD_LIST);
    setClickedBoardId(EMPTY);
  }, [replacePath, setClickedBoardId]);

  const openBoardDetailsScreen = useSafeCallback(
    (boardId: BoardId): void => {
      replacePath(embedIdInPath(Path.BOARD_DETAILS, PATH_IDS, [boardId]));
      setClickedBoardId(boardId);
      !!user && database.ref(toNewMessageNotificationsPath(user.userId, boardId)).set(CONFIRMED);
    },
    [replacePath, setClickedBoardId, user]
  );

  const openBoardFullModalScreen = useSafeCallback(
    (action: BoardAction): void => {
      setAction(action);
      setIsFullModalOpen(true);
    },
    [setAction, setIsFullModalOpen]
  );

  const openBoardPartialModalScreen = useSafeCallback(
    (action: BoardAction): void => {
      setAction(action);
      setIsPartialModalOpen(true);
    },
    [setAction, setIsPartialModalOpen]
  );

  const openModalScreen = useSafeCallback(
    (photoURL: URL): void => {
      setClickedPhotoURL(photoURL);
      setIsModalOpen(true);
    },
    [setClickedPhotoURL, setIsModalOpen]
  );

  const handleActionEmitted = useSafeCallback(
    (action: BoardAction): void => {
      switch (action) {
        case BoardAction.BACK_TO_HOME:
          openBoardListScreen();
          return;

        case BoardAction.BACK_TO_DETAILS:
          if (!clickedBoardId) return;
          !!boardMessageListRef.current && boardMessageListRef.current.refresh();
          openBoardDetailsScreen(clickedBoardId);
          return;

        case BoardAction.OPEN_WISH:
          return;

        case BoardAction.CREATE_BOARD:
        case BoardAction.OPEN_BOARD_SETTINGS:
          openBoardFullModalScreen(action);
          return;

        case BoardAction.INVITE_MEMBERS:
        case BoardAction.SHOW_QR:
          openBoardPartialModalScreen(action);
          return;

        case BoardAction.REFRESH_BOARD_LIST:
          !!boardListRef.current && boardListRef.current.refresh();
          return;

        case BoardAction.REFRESH_BOARD_DETAILS:
          !!boardMessageRef.current && boardMessageRef.current.refresh();
          !!boardDetailsRef.current && boardDetailsRef.current.refresh();
          return;

        case BoardAction.REFRESH_BOARD_THREADS:
          !!boardThreadRef.current && boardThreadRef.current.refresh();
          return;
      }
    },
    [openBoardListScreen, openBoardDetailsScreen, clickedBoardId, openBoardFullModalScreen, openBoardPartialModalScreen]
  );

  const openBoardMessageScreen = useSafeCallback(
    (boardId: BoardId): void => {
      replacePath(embedIdInPath(Path.BOARD_MESSAGE, PATH_IDS, [boardId]));
      setClickedBoardId(boardId);
    },
    [replacePath, setClickedBoardId]
  );

  const openBoardReplyScreen = useSafeCallback(
    (boardId: BoardId, boardMessageId: BoardMessageId): void => {
      replacePath(embedIdInPath(Path.BOARD_THREAD, PATH_IDS, [boardId, boardMessageId]));
      setClickedBoardId(boardId);
      setClickedBoardMessageId(boardMessageId);
    },
    [replacePath, setClickedBoardId, setClickedBoardMessageId]
  );

  const handleBoardClicked = useSafeCallback(
    (board: BoardEntity) => {
      return board.isDraft ? openBoardMessageScreen(board.boardId) : openBoardDetailsScreen(board.boardId);
    },
    [openBoardMessageScreen, openBoardDetailsScreen]
  );

  return (
    <Screen className='home-screen'>
      <BoardHeader boardId={clickedBoardId} emitAction={handleActionEmitted} />

      <TransitionGroup
        index={index}
        components={[
          <BoardList
            ref={boardListRef}
            user={user}
            onClickBoard={handleBoardClicked}
            emitAction={handleActionEmitted}
          />,
          <BoardMessage
            ref={boardMessageRef}
            boardId={clickedBoardId}
            user={user}
            emitAction={handleActionEmitted}
            goBack={openBoardListScreen}
          />,
          <BoardDetails
            ref={boardDetailsRef}
            messageListRef={boardMessageListRef}
            boardId={clickedBoardId}
            user={user}
            onClick={openModalScreen}
            onReply={openBoardReplyScreen}
            emitAction={handleActionEmitted}
          />,
          <BoardThread ref={boardThreadRef} boardMessageId={clickedBoardMessageId} />
        ]}
      />

      {!!user && (
        <BoardFullModal
          isModalOpen={isFullModalOpen}
          action={action}
          userId={user.userId}
          boardId={clickedBoardId}
          emitAction={handleActionEmitted}
          onClose={() => setIsFullModalOpen(false)}
        />
      )}

      {!!user && (
        <BoardPartialModal
          isModalOpen={isPartialModalOpen}
          action={action}
          boardId={clickedBoardId}
          userId={user.userId}
          onClose={() => setIsPartialModalOpen(false)}
        />
      )}

      {!!user && (
        <BoardFixedModal isModalOpen={isModalOpen} photoURL={clickedPhotoURL} onClose={() => setIsModalOpen(false)} />
      )}

      {!!user && index === BoardIndex.BOARD_REPLY && (
        <BoardFooterInput
          boardMessageId={clickedBoardMessageId}
          userId={user.userId}
          emitAction={handleActionEmitted}
        />
      )}
    </Screen>
  );
});

export default HomeScreen;
